import { useEffect, useRef } from 'react';
import {
  Combobox,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
} from '../ui/combobox';
import { Field, FieldLabel } from '../ui/field';
import { Spinner } from '../ui/spinner';

export interface ValueSetCodeDesignation {
  use?: { system?: string; code?: string };
  value?: string;
}

export interface ValueSetCode {
  system?: string;
  code: string;
  display?: string;
  designation?: ValueSetCodeDesignation[];
}

/** A labelled bucket of items rendered as a section inside the dropdown. */
export interface ValueSetCodeGroup {
  /** Stable id for the group. Used for keying; not user-visible. */
  id: string;
  /** Section header rendered above the group's items. */
  label: string;
  items: ValueSetCode[];
}

interface ValueSetComboboxBaseProps {
  id: string;
  label: string;
  placeholder?: string;
  value: ValueSetCode | null;
  onValueChange: (value: ValueSetCode | null) => void;
  onQueryChange: (query: string) => void;
  isLoading: boolean;
  error?: string;
  emptyMessage: string;
  required?: boolean;
  disabled?: boolean;
  /**
   * Seeds the visible input text on first render and fires `onQueryChange(initialQuery)` once on
   * mount so the upstream typeahead is pre-filtered without waiting for a keystroke. Used by
   * draft-confirmation flows where the AI has already proposed a search term.
   */
  initialQuery?: string;
  /**
   * Custom renderer for a dropdown row. Defaults to the plain `itemLabel(item)` text. Use to render
   * richer rows (e.g. a trailing badge + a subtitle line) — the selected value's input text still
   * comes from `itemToStringLabel`, so `display` should stay the primary label.
   */
  renderItem?: (item: ValueSetCode) => React.ReactNode;
}

export type ValueSetComboboxProps =
  | (ValueSetComboboxBaseProps & { items: ValueSetCode[]; groups?: never })
  | (ValueSetComboboxBaseProps & { items?: never; groups: ValueSetCodeGroup[] });

const itemLabel = (item: ValueSetCode): string => item.display ?? item.code;
const itemKey = (item: ValueSetCode): string => `${item.system ?? ''}|${item.code}`;

export function ValueSetCombobox(props: ValueSetComboboxProps): React.ReactNode {
  const {
    id,
    label,
    placeholder,
    value,
    onValueChange,
    onQueryChange,
    isLoading,
    error,
    emptyMessage,
    required,
    disabled,
    initialQuery,
    renderItem,
  } = props;
  const renderRow = renderItem ?? itemLabel;

  // base-ui Combobox detects grouped items via the presence of a top-level `items` key on the
  // first element (see `isGroupedItems` in @base-ui/react/internals/resolveValueLabel). Passing
  // either shape lets the primitive index and filter correctly.
  const comboboxItems = 'groups' in props && props.groups ? props.groups : props.items;

  // Fire `onQueryChange(initialQuery)` exactly once on mount so the upstream typeahead is
  // pre-filtered. The ref guard prevents React strict-mode double-fire from issuing two upstream
  // calls. `initialQuery` is captured at mount; later changes are intentionally ignored — the
  // visible input is uncontrolled via `defaultInputValue`, so re-seeding after mount would
  // desynchronize the two.
  const didSeedRef = useRef(false);
  useEffect(() => {
    if (didSeedRef.current) return;
    didSeedRef.current = true;
    if (initialQuery) onQueryChange(initialQuery);
  }, [initialQuery, onQueryChange]);

  return (
    <Field>
      <FieldLabel htmlFor={id}>
        {label}
        {required && <span aria-hidden="true">*</span>}
      </FieldLabel>
      <Combobox<ValueSetCode>
        items={comboboxItems}
        value={value}
        onValueChange={onValueChange}
        onInputValueChange={onQueryChange}
        defaultInputValue={initialQuery}
        itemToStringLabel={itemLabel}
        itemToStringValue={(item) => item.code}
        isItemEqualToValue={(a, b) => a.code === b.code && a.system === b.system}
        filter={() => true}
      >
        <ComboboxInput
          id={id}
          placeholder={placeholder}
          aria-label={label}
          aria-required={required}
          disabled={disabled}
          showClear={!!value}
        />
        <ComboboxContent
          // https://github.com/shadcn-ui/ui/issues/9770#issuecomment-4214505872
          className="pointer-events-auto"
          onWheel={(e) => e.stopPropagation()}
        >
          <ComboboxList>
            <ComboboxEmpty>
              {isLoading ? (
                <Spinner className="size-4" />
              ) : error ? (
                <span className="text-destructive">{error}</span>
              ) : (
                emptyMessage
              )}
            </ComboboxEmpty>
            {'groups' in props && props.groups ? (
              <ComboboxCollection>
                {(group: ValueSetCodeGroup) => (
                  <ComboboxGroup key={group.id} items={group.items}>
                    <ComboboxLabel>{group.label}</ComboboxLabel>
                    {/* `items` on ComboboxGroup wires up GroupCollectionProvider so the
                       nested Collection iterates this group's items, not the root list. */}
                    <ComboboxCollection>
                      {(item: ValueSetCode) => (
                        <ComboboxItem key={itemKey(item)} value={item}>
                          {renderRow(item)}
                        </ComboboxItem>
                      )}
                    </ComboboxCollection>
                  </ComboboxGroup>
                )}
              </ComboboxCollection>
            ) : (
              <ComboboxCollection>
                {(item) => (
                  <ComboboxItem key={itemKey(item)} value={item}>
                    {renderRow(item)}
                  </ComboboxItem>
                )}
              </ComboboxCollection>
            )}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
    </Field>
  );
}
